/**
 * Subscribe loop: the consumer-side substrate primitive (generic claim layer).
 *
 * A `subscribe(...)` call creates a long-running session that:
 *  1. Registers as a member of each pool. Membership is the whole routing
 *     contract (ADR 0004/0005) — no capability or strategy matching here.
 *  2. Discovers dispatchable jobs (poll today; NATS/SSE push later).
 *  3. For each claimed job, hands the OPAQUE payload to `onJob` and submits the
 *     result it returns. The loop knows nothing about strategies, executors, or
 *     prompts — that lives in the Shuttle callback (ADR 0005).
 *
 * The loop owns: pool membership, discovery, concurrency, claim (lease), and the
 * submit/close of attendance. Everything content-specific is the callback's.
 */

import type { JobsClient } from './client';
import type { DispatchableJob, DispatchedJob, PoolRef } from './types';

export interface JobResult {
  status: 'completed' | 'failed' | 'abandoned';
  /** Output rows the loop submits to Jobs before closing attendance. */
  outputs?: Array<{ label: string; content: Record<string, unknown> }>;
  reason?: string;
}

export interface SubscribeLogger {
  debug?(msg: string, meta?: Record<string, unknown>): void;
  info(msg: string, meta?: Record<string, unknown>): void;
  warn(msg: string, meta?: Record<string, unknown>): void;
  error(msg: string, meta?: Record<string, unknown>): void;
}

export interface SubscribeOptions {
  jobsClient: JobsClient;
  /** Pools to subscribe to. Names are resolved at start; ids pass through. */
  pools: PoolRef[];
  /**
   * Member declaration. Used to register as a pool member. Routing reads only
   * membership, never these attributes.
   */
  subscriber: {
    identityType: 'user' | 'agent';
    identityId: string;
    intelligence: 'biological' | 'digital';
    displayName?: string;
    metadata?: Record<string, unknown>;
  };
  /**
   * Handle one claimed job: interpret the opaque payload, do the work, and
   * return the result. The loop creates the returned outputs and closes
   * attendance with the returned status. `signal` aborts ~30s before the lease
   * deadline. A thrown error is treated as a failed result.
   */
  onJob: (dispatch: DispatchedJob, signal: AbortSignal) => Promise<JobResult>;
  /**
   * Invoked when a poll response carries pending member directives —
   * product-written instructions for the subscriber itself (e.g. an
   * upgrade request), merged across this caller's active memberships.
   * Fired once per poll that reports them; the handler owns dedupe. A
   * thrown error is logged and does not stop the loop.
   */
  onDirectives?: (directives: Record<string, unknown>) => void | Promise<void>;
  logger?: SubscribeLogger;
  /** Polling cadence (ms). Default 2000. */
  pollIntervalMs?: number;
  /** Max parallel in-flight jobs across all pools. Default 1. */
  maxConcurrency?: number;
}

export interface Subscription {
  start(): Promise<void>;
  stop(): Promise<void>;
}

const NULL_LOGGER: SubscribeLogger = {
  debug: () => {},
  info: () => {},
  warn: () => {},
  error: () => {},
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Construct a long-running subscribe session. Call `.start()` to register
 * membership and begin claiming; `.stop()` to gracefully drain.
 */
export function subscribe(opts: SubscribeOptions): Subscription {
  const logger = opts.logger ?? NULL_LOGGER;
  const pollIntervalMs = opts.pollIntervalMs ?? 2_000;
  const maxConcurrency = opts.maxConcurrency ?? 1;

  let stopped = true;
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let inflight = 0;
  let resolvedPoolIds: string[] = [];

  async function start(): Promise<void> {
    if (!stopped) return;
    stopped = false;

    resolvedPoolIds = [];
    for (const ref of opts.pools) {
      const id = await opts.jobsClient.resolvePoolRef(ref);
      resolvedPoolIds.push(id);
      await opts.jobsClient.registerMember(id, {
        identityType: opts.subscriber.identityType,
        identityId: opts.subscriber.identityId,
        intelligence: opts.subscriber.intelligence,
        ...(opts.subscriber.displayName !== undefined
          ? { displayName: opts.subscriber.displayName }
          : {}),
        ...(opts.subscriber.metadata !== undefined
          ? { metadata: opts.subscriber.metadata }
          : {}),
      });
    }

    logger.info('subscribe loop started', { pools: resolvedPoolIds, maxConcurrency });
    schedulePoll(pollIntervalMs);
  }

  async function stop(): Promise<void> {
    if (stopped) return;
    stopped = true;
    if (pollTimer !== null) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
    while (inflight > 0) {
      await sleep(100);
    }
    logger.info('subscribe loop stopped');
  }

  function schedulePoll(delayMs: number): void {
    pollTimer = setTimeout(() => {
      void poll();
    }, delayMs);
  }

  async function poll(): Promise<void> {
    if (stopped) return;
    if (inflight >= maxConcurrency) {
      schedulePoll(pollIntervalMs);
      return;
    }
    try {
      const { data, directives } = await opts.jobsClient.listDispatchableJobs({
        pools: resolvedPoolIds,
        limit: maxConcurrency - inflight,
        sort: 'priority:desc,queued_at:asc',
      });
      if (directives && Object.keys(directives).length > 0 && opts.onDirectives) {
        try {
          await opts.onDirectives(directives);
        } catch (err) {
          logger.error('onDirectives handler failed', {
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }
      if (stopped) return; // a directive handler may have stopped the loop
      for (const job of data) {
        if (inflight >= maxConcurrency) break;
        inflight++;
        void runOnce(job).finally(() => {
          inflight--;
        });
      }
    } catch (err) {
      logger.error('poll failed', { error: err instanceof Error ? err.message : String(err) });
    }
    if (!stopped) schedulePoll(pollIntervalMs);
  }

  async function runOnce(available: DispatchableJob): Promise<void> {
    let dispatch: DispatchedJob;
    try {
      dispatch = await opts.jobsClient.acceptDispatch(available.id);
    } catch (err) {
      const statusCode = (err as Error & { statusCode?: number }).statusCode;
      if (statusCode === 409) {
        logger.debug?.('claim conflict; another member accepted', { jobId: available.id });
        return;
      }
      logger.error('failed to accept dispatch', {
        jobId: available.id,
        error: err instanceof Error ? err.message : String(err),
      });
      return;
    }

    // Abort ~30s before the lease deadline so the callback can wind down.
    const controller = new AbortController();
    let deadlineTimer: ReturnType<typeof setTimeout> | null = null;
    if (dispatch.deadline) {
      const ms = new Date(dispatch.deadline).getTime() - Date.now() - 30_000;
      if (ms > 0) {
        deadlineTimer = setTimeout(() => controller.abort(), ms);
      } else {
        controller.abort();
      }
    }

    let result: JobResult;
    try {
      result = await opts.onJob(dispatch, controller.signal);
    } catch (err) {
      result = {
        status: 'failed',
        reason: `onJob threw: ${err instanceof Error ? err.message : String(err)}`,
      };
    } finally {
      if (deadlineTimer !== null) clearTimeout(deadlineTimer);
    }

    try {
      for (const output of result.outputs ?? []) {
        await opts.jobsClient.createOutput(dispatch.job.id, {
          label: output.label,
          content: output.content,
        });
      }
      await opts.jobsClient.submit(dispatch.job.id, {
        status: result.status,
        ...(result.reason !== undefined ? { reason: result.reason } : {}),
      });
    } catch (err) {
      logger.error('submit/close failed', {
        jobId: dispatch.job.id,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  return { start, stop };
}
